import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import PremiumModelNudge from './PremiumModelNudge';
import {
  getActiveGeneration,
  clearActiveGeneration,
} from '../services/activeGeneration';
import './GenerationBanner.css';

/**
 * "Your book is being written" — shown on every page, not just /bookwriter.
 *
 * The failure this exists to prevent: a book takes up to half an hour to
 * write, and book_generations gets no row until it finishes, so /my-books is
 * genuinely empty the whole time. Leave the writer and the app goes silent.
 * A trial user did exactly that on 2026-09-19 and cancelled 80 seconds before
 * their 20-chapter book landed.
 *
 * Mounted once at app level so it survives route changes. It asks the server
 * rather than trusting localStorage, because that user was signed out twice
 * mid-generation and a per-tab key would have vanished with the session.
 */

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  (window.location.hostname === 'localhost' ? 'http://localhost:5050/api' : '/api');

const POLL_MS = 6000;

type Phase = 'idle' | 'working' | 'done' | 'failed';

const GenerationBanner: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [phase, setPhase] = useState<Phase>('idle');
  const [progress, setProgress] = useState(0);
  const [message, setMessage] = useState('');
  const [title, setTitle] = useState('');
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [bookId, setBookId] = useState<number | null>(null);
  const [minimised, setMinimised] = useState(false);

  // Guards the "it finished" state so a poll that returns active:false right
  // after completion does not wipe the banner before the user has seen it.
  const sawWorkingRef = useRef(false);

  const authHeaders = useCallback((): Record<string, string> => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const hydrateFromLocal = useCallback(() => {
    const local = getActiveGeneration();
    if (!local) return;
    setPhase(p => (p === 'done' ? p : 'working'));
    setTitle(t => t || local.title);
    setStartedAt(s => s ?? local.startedAt);
    setMessage(m => m || 'Writing your book…');
  }, []);

  const poll = useCallback(async () => {
    if (!user) return;
    try {
      const res = await fetch(`${API_BASE_URL}/generate/active`, { headers: authHeaders() });
      if (!res.ok) return;
      const data = await res.json();

      if (data.active) {
        sawWorkingRef.current = true;
        setPhase('working');
        setProgress(Math.min(99, Math.max(0, data.progress || 0)));
        setMessage(data.message || 'Writing your book…');
        if (data.bookTitle) setTitle(data.bookTitle);
        if (data.startedAt) setStartedAt(new Date(data.startedAt).getTime());
        return;
      }

      // Not active any more. If we watched it working, it just finished.
      if (sawWorkingRef.current) {
        sawWorkingRef.current = false;
        clearActiveGeneration();
        setPhase('done');
        setProgress(100);
        return;
      }

      // Never saw it working: either nothing is running, or this tab's local
      // copy is stale (a backend restart drops in-memory sessions).
      const local = getActiveGeneration();
      if (local) {
        // Give a just-started generation a moment to register server-side
        // before concluding the local copy is stale.
        if (Date.now() - local.startedAt < 30000) {
          setPhase('working');
          setTitle(local.title);
          setStartedAt(local.startedAt);
          setMessage('Starting your book…');
          return;
        }
        clearActiveGeneration();
      }
      // Never clobber a finished state. clearActiveGeneration() above fires
      // 'sai:generation-changed', which re-enters this poll immediately — with
      // sawWorkingRef already reset — so a plain setPhase('idle') erased the
      // "your book is ready" card a moment after showing it. 'done' only ends
      // when the user reads or dismisses it.
      setPhase(p => (p === 'done' ? 'done' : 'idle'));
    } catch {
      // Offline, CORS, or a blip. Fall back to this tab's own copy rather than
      // showing nothing — "nothing" is the exact failure this banner exists to
      // prevent.
      hydrateFromLocal();
    }
  }, [user, authHeaders, hydrateFromLocal]);

  useEffect(() => {
    if (!user) return; // Not signed out — just not loaded yet; see below.
    // Paint immediately from the local copy, then let the server correct it.
    // This has to live here rather than in a mount-only effect: AuthProvider
    // is a parent, and a parent's effects run AFTER its children's, so `user`
    // is still null on our first pass. Hydrating on mount and then bailing on
    // !user cleared the banner before it ever appeared.
    hydrateFromLocal();
    poll();
    const id = setInterval(poll, POLL_MS);
    const onChange = () => poll();
    window.addEventListener('sai:generation-changed', onChange);
    window.addEventListener('storage', onChange);
    return () => {
      clearInterval(id);
      window.removeEventListener('sai:generation-changed', onChange);
      window.removeEventListener('storage', onChange);
    };
  }, [user, poll]);

  // Elapsed clock. Half an hour of silence is the thing that loses people, so
  // the time is shown rather than hidden.
  useEffect(() => {
    if (phase !== 'working' || !startedAt) return;
    const tick = () => setElapsed(Math.max(0, Math.floor((Date.now() - startedAt) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [phase, startedAt]);

  // Fetch the newest book once finished, so "Read it" can go straight there.
  useEffect(() => {
    if (phase !== 'done') return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/book/my-books`, { headers: authHeaders() });
        if (!res.ok) return;
        const data = await res.json();
        const newest = (data.books || [])[0];
        if (!cancelled && newest) { setBookId(newest.id); setTitle(newest.title || title); }
      } catch { /* the My Books link still works without it */ }
    })();
    return () => { cancelled = true; };
  }, [phase, authHeaders, title]);

  if (phase === 'idle' || !user) return null;

  const mmss = `${Math.floor(elapsed / 60)}m ${String(elapsed % 60).padStart(2, '0')}s`;
  const done = phase === 'done';

  return (
    <aside
      className={`genban ${done ? 'genban-done' : ''} ${minimised ? 'genban-min' : ''}`}
      data-generation-working={phase === 'working' ? 'true' : undefined}
      role="status"
      aria-live="polite"
    >
      <button
        type="button"
        className="genban-toggle"
        aria-label={minimised ? 'Show book progress' : 'Minimise book progress'}
        onClick={() => setMinimised(m => !m)}
      >
        <span className="genban-quill" aria-hidden="true">🪶</span>
      </button>

      {!minimised && (
        <div className="genban-body">
          <div className="genban-head">
            <span className="genban-title">
              {done ? 'Your book is ready' : 'Writing your book'}
            </span>
            {!done && <span className="genban-time">{mmss}</span>}
          </div>

          {title && <div className="genban-booktitle">&ldquo;{title}&rdquo;</div>}

          {done ? (
            <>
              <p className="genban-note">
                It&rsquo;s saved in your books. Nothing was lost.
              </p>
              <div className="genban-actions">
                <button
                  type="button"
                  className="genban-cta"
                  onClick={() => { setPhase('idle'); navigate(bookId ? `/book/${bookId}` : '/my-books'); }}
                >
                  Read it
                </button>
                <button type="button" className="genban-dismiss" onClick={() => setPhase('idle')}>
                  Dismiss
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="genban-track" aria-hidden="true">
                <div className="genban-fill" style={{ width: `${Math.max(4, progress)}%` }} />
              </div>
              <div className="genban-msg">{message}</div>
              {/* The sentence that would have saved the 2026-09-19 trial. */}
              <p className="genban-note">
                Keep browsing — it keeps writing. It will appear in{' '}
                <button type="button" className="genban-link" onClick={() => navigate('/my-books')}>
                  My Books
                </button>{' '}
                when it&rsquo;s done. A full novel can take 20&ndash;30 minutes.
              </p>
              {phase === 'working' && <PremiumModelNudge source="book_generation" />}
            </>
          )}
        </div>
      )}
    </aside>
  );
};

export default GenerationBanner;
